import { useState, useEffect } from 'react';
import { db } from '@/lib/firebase';
import { useTranslation } from 'react-i18next';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { 
  Instagram, 
  Facebook, 
  MessageCircle, 
  Video, 
  Save, 
  Loader2, 
  Globe,
  MapPin,
  Phone,
  Clock
} from 'lucide-react';
import { BusinessHours } from '@/hooks/useBusinessInfo';

export default function BusinessInfoPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [info, setInfo] = useState({
    instagram: '',
    facebook: '',
    tiktok: '',
    whatsapp: '',
    address: '',
    phone: '',
    hours: {
      monday: { isOpen: false, openTime: '11:00', closeTime: '23:00' },
      tuesday: { isOpen: true, openTime: '11:00', closeTime: '23:00' },
      wednesday: { isOpen: true, openTime: '11:00', closeTime: '23:00' },
      thursday: { isOpen: true, openTime: '11:00', closeTime: '23:00' },
      friday: { isOpen: true, openTime: '11:00', closeTime: '23:00' },
      saturday: { isOpen: true, openTime: '11:00', closeTime: '23:00' },
      sunday: { isOpen: true, openTime: '11:00', closeTime: '23:00' },
    }
  });

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'business_info'), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setInfo((prev: any) => ({ ...prev, ...data, hours: data.hours || prev.hours }));
      }
      setLoading(false);
    });

    return () => unsub();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSuccess(false);

    try {
      await setDoc(doc(db, 'settings', 'business_info'), {
        ...info,
        updatedAt: new Date().toISOString()
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error("Error saving business info:", error);
      alert("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#F57F25]" size={48} />
      </div>
    );
  }

  return (
    <div className="max-w-5xl space-y-10">
      <div>
        <h1 className="text-3xl font-black text-white uppercase tracking-tight">{t('admin_business_title')}</h1>
        <p className="text-white/50 text-sm">{t('admin_business_sub')}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-10">
        
        {/* Physical Contact Section */}
        <section className="space-y-6">
          <h2 className="text-xs font-black text-[#F57F25] uppercase tracking-[0.2em] border-b border-white/5 pb-2">{t('admin_business_location')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-[#111] p-8 rounded-[32px] border border-white/5 space-y-4">
              <div className="flex items-center gap-3 text-white mb-2">
                <MapPin size={24} className="text-[#F57F25]" />
                <h3 className="font-black uppercase tracking-widest text-xs">{t('admin_business_address')}</h3>
              </div>
              <input
                type="text"
                value={info.address}
                onChange={e => setInfo({ ...info, address: e.target.value })}
                className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none transition-all text-sm"
                placeholder={t('admin_business_address_placeholder')}
              />
            </div>

            <div className="bg-[#111] p-8 rounded-[32px] border border-white/5 space-y-4">
              <div className="flex items-center gap-3 text-white mb-2">
                <Phone size={24} className="text-[#F57F25]" />
                <h3 className="font-black uppercase tracking-widest text-xs">{t('admin_business_phone')}</h3>
              </div>
              <input
                type="text"
                value={info.phone}
                onChange={e => setInfo({ ...info, phone: e.target.value })}
                className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none transition-all text-sm"
                placeholder={t('admin_business_phone_placeholder')}
              />
            </div>
          </div>
        </section>

        {/* Social Media Section */}
        <section className="space-y-6">
          <h2 className="text-xs font-black text-[#F57F25] uppercase tracking-[0.2em] border-b border-white/5 pb-2">{t('admin_business_social')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-[#111] p-8 rounded-[32px] border border-white/5 space-y-4">
              <div className="flex items-center gap-3 text-white mb-2">
                <Instagram size={24} className="text-[#F57F25]" />
                <h3 className="font-black uppercase tracking-widest text-xs">Instagram</h3>
              </div>
              <input
                type="url"
                value={info.instagram}
                onChange={e => setInfo({ ...info, instagram: e.target.value })}
                className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none transition-all text-sm"
                placeholder="https://instagram.com/bucaraburger"
              />
            </div>

            <div className="bg-[#111] p-8 rounded-[32px] border border-white/5 space-y-4">
              <div className="flex items-center gap-3 text-white mb-2">
                <Facebook size={24} className="text-[#F57F25]" />
                <h3 className="font-black uppercase tracking-widest text-xs">Facebook</h3>
              </div>
              <input
                type="url"
                value={info.facebook}
                onChange={e => setInfo({ ...info, facebook: e.target.value })}
                className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none transition-all text-sm"
                placeholder="https://facebook.com/bucaraburger"
              />
            </div>

            <div className="bg-[#111] p-8 rounded-[32px] border border-white/5 space-y-4">
              <div className="flex items-center gap-3 text-white mb-2">
                <Video size={24} className="text-[#F57F25]" />
                <h3 className="font-black uppercase tracking-widest text-xs">TikTok</h3>
              </div>
              <input
                type="url"
                value={info.tiktok}
                onChange={e => setInfo({ ...info, tiktok: e.target.value })}
                className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none transition-all text-sm"
                placeholder="https://tiktok.com/@bucaraburger"
              />
            </div>

            <div className="bg-[#111] p-8 rounded-[32px] border border-white/5 space-y-4">
              <div className="flex items-center gap-3 text-white mb-2">
                <MessageCircle size={24} className="text-[#F57F25]" />
                <h3 className="font-black uppercase tracking-widest text-xs">{t('admin_business_whatsapp')}</h3>
              </div>
              <input
                type="text"
                value={info.whatsapp}
                onChange={e => setInfo({ ...info, whatsapp: e.target.value })}
                className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none transition-all text-sm"
                placeholder={t('admin_business_whatsapp_placeholder')}
              />
            </div>
          </div>
        </section>

        {/* Business Hours Section */}
        <section className="space-y-6">
          <h2 className="text-xs font-black text-[#F57F25] uppercase tracking-[0.2em] border-b border-white/5 pb-2">Business Hours</h2>
          
          <div className={`p-8 rounded-[32px] border flex flex-col md:flex-row md:items-center justify-between transition-colors ${info.forceClosed ? 'bg-red-500/10 border-red-500/30' : 'bg-[#111] border-white/5'}`}>
            <div className="mb-4 md:mb-0">
              <h3 className={`font-black uppercase tracking-widest text-sm mb-1 ${info.forceClosed ? 'text-red-400' : 'text-[#F57F25]'}`}>
                {info.forceClosed ? 'Store is Forced Closed' : 'Store is Active'}
              </h3>
              <p className="text-white/50 text-xs max-w-md">
                Turn off to force the store to appear "Closed" across the website, ignoring the regular schedule below.
              </p>
            </div>
            
            <label className="relative inline-flex items-center cursor-pointer shrink-0">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={!info.forceClosed} 
                onChange={e => setInfo({ ...info, forceClosed: !e.target.checked })} 
              />
              <div className="w-14 h-7 bg-red-500/40 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-500"></div>
            </label>
          </div>

          <div className="bg-[#111] p-8 rounded-[32px] border border-white/5 space-y-4">
            <div className="flex items-center gap-3 text-white mb-6">
              <Clock size={24} className="text-[#F57F25]" />
              <h3 className="font-black uppercase tracking-widest text-xs">Schedule</h3>
            </div>
            
            <div className="space-y-4">
              {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                const dayKey = day as keyof BusinessHours;
                const dayData = (info.hours as any)[dayKey];
                return (
                  <div key={day} className="flex items-center gap-4 bg-black p-4 rounded-2xl border border-white/10">
                    <div className="w-28 text-white font-semibold capitalize text-sm">{t(`day_${day}`)}</div>
                    
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={dayData.isOpen} 
                        onChange={(e) => setInfo({
                          ...info,
                          hours: {
                            ...(info.hours as any),
                            [dayKey]: { ...dayData, isOpen: e.target.checked }
                          }
                        })}
                        className="w-4 h-4 accent-[#F57F25]"
                      />
                      <span className="text-white/70 text-sm">{dayData.isOpen ? 'Open' : 'Closed'}</span>
                    </label>

                    {dayData.isOpen && (
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:ml-auto mt-2 sm:mt-0">
                        <input
                          type="time"
                          value={dayData.openTime}
                          onChange={(e) => setInfo({
                            ...info,
                            hours: {
                              ...(info.hours as any),
                              [dayKey]: { ...dayData, openTime: e.target.value }
                            }
                          })}
                          className="bg-[#111] border border-white/10 rounded-lg py-2 px-3 text-white focus:border-[#F57F25] outline-none text-sm"
                        />
                        <span className="text-white/50 text-xs sm:text-sm">to</span>
                        <input
                          type="time"
                          value={dayData.closeTime}
                          onChange={(e) => setInfo({
                            ...info,
                            hours: {
                              ...(info.hours as any),
                              [dayKey]: { ...dayData, closeTime: e.target.value }
                            }
                          })}
                          className="bg-[#111] border border-white/10 rounded-lg py-2 px-3 text-white focus:border-[#F57F25] outline-none text-sm"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <div className="flex items-center gap-6 pt-4">
          <button
            type="submit"
            disabled={saving}
            className="bg-[#F57F25] hover:bg-[#e06d15] text-black font-black px-10 py-5 rounded-3xl transition-all flex items-center justify-center gap-3 uppercase text-sm shadow-xl shadow-[#F57F25]/20 disabled:opacity-50"
          >
            {saving ? <Loader2 className="animate-spin" size={20} /> : <><Save size={20} /> {t('admin_business_update')}</>}
          </button>
          
          {success && (
            <div className="flex items-center gap-2 text-green-500 font-bold animate-bounce">
              <Globe size={20} />
              <span className="text-xs uppercase tracking-widest">{t('admin_business_success')}</span>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
